import { FieldType } from "#gen/forms/v1/field_pb.js";
import type { Schema } from "#lib/components/jsonForms/jsonSchema.js";

export enum OperandType {
	TEXT = "string",
	NUMBER = "number",
	DATE = "date",
}

export enum RuleOperator {
	CONTAINS = "contains",
	NOT_CONTAINS = "not_contains",
	STRING_EQ = "string_eq",
	NUM_EQ = "num_eq",
	STRING_NEQ = "string_neq",
	NUM_NEQ = "num_neq",
	BEGINS = "begins_with",
	ENDS = "ends_with",
	REGEX = "regex",
	LT = "lt",
	LTE = "lte",
	GT = "gt",
	GTE = "gte",
	BETWEEN = "between",
	CHAR_COUNT_EXACT = "char_count_exact",
	CHAR_COUNT_MIN = "char_count_min",
	CHAR_COUNT_MAX = "char_count_max",
	DIGIT_COUNT_EXACT = "digit_count_exact",
	DIGIT_COUNT_MIN = "digit_count_min",
	DIGIT_COUNT_MAX = "digit_count_max",
	DATE_AT_LEAST = "date_at_least",
	DATE_WITHIN = "date_within",
	DATE_MORE_THAN = "date_more_than",
	DATE_NO_MORE_THAN = "date_no_more_than",
	DATE_EQ = "date_eq",
	DATE_NEQ = "date_neq",
	DATE_BETWEEN = "date_between",
}

export enum DurationUnit {
	DAYS = "days",
	WEEKS = "weeks",
	MONTHS = "months",
	YEARS = "years",
}

export type BetweenValue = { min: number | string | undefined; max: number | string | undefined };
export type DurationValue = {
	operator: RuleOperator.DATE_AT_LEAST | RuleOperator.DATE_MORE_THAN | RuleOperator.DATE_NO_MORE_THAN | RuleOperator.DATE_WITHIN;
	value: number;
	unit: DurationUnit;
};

export type RuleValueType = string | number | undefined | BetweenValue | string[] | DurationValue;

interface OperatorOption {
	operandTypes: OperandType[];
	getValue: (s: Schema) => RuleValueType;
	getSchema: SchemaProducer;
	label: { single: string; multiple?: string };
	fullLabel: { alpha?: string; numeric?: string };
	charCountSecondaryLabel?: string;
	value: (typeof RuleOperator)[keyof typeof RuleOperator];
	getErrorMessage: (s: Schema) => string | undefined;
	valueType: "string" | "number" | "array" | "object";
}

function toStringArray(value: unknown): string[] {
	if (!Array.isArray(value)) {
		return [];
	}
	return value.filter((item): item is string => typeof item === "string");
}

function toNumber(value: unknown): number | undefined {
	return typeof value === "number" ? value : undefined;
}

export const operators: Record<RuleOperator, OperatorOption> = {
	[RuleOperator.CONTAINS]: {
		operandTypes: [OperandType.TEXT],
		value: RuleOperator.CONTAINS,
		label: { single: "contain", multiple: "contain any of" },
		fullLabel: { alpha: "contain" },
		getSchema: (values, errorMsg) => ({
			type: "string",
			pattern: `(${toPatternGroup(toValueArray(values))})`,
			errorMessage: { pattern: errorMsg },
		}),
		getValue: (s: Schema): string[] => {
			const group = extractGroup(s.pattern ?? "");
			return group ? fromPatternGroup(group) : [];
		},
		getErrorMessage: (s: Schema) => s.errorMessage?.pattern,
		valueType: "array",
	},
	[RuleOperator.NOT_CONTAINS]: {
		operandTypes: [OperandType.TEXT],
		value: RuleOperator.NOT_CONTAINS,
		label: { single: "not contain", multiple: "not contain any of" },
		fullLabel: { alpha: "not contain" },
		getSchema: (values, errorMsg) => ({
			type: "string",
			not: {
				pattern: `(${toPatternGroup(toValueArray(values))})`,
			},
			errorMessage: { not: errorMsg },
		}),
		getValue: (s: Schema): string[] => {
			const group = extractGroup(s.not?.pattern ?? "");
			return group ? fromPatternGroup(group) : [];
		},
		getErrorMessage: (s: Schema) => s.errorMessage?.not,
		valueType: "array",
	},
	[RuleOperator.STRING_EQ]: {
		operandTypes: [OperandType.TEXT],
		value: RuleOperator.STRING_EQ,
		label: { single: "be equal to", multiple: "be equal to one of" },
		fullLabel: { alpha: "be equal to" },
		getSchema: (values, errorMsg) => {
			return { type: "string", enum: Array.isArray(values) ? values : [values], errorMessage: { enum: errorMsg } };
		},
		getValue: (s: Schema): string[] => toStringArray(s.enum),
		getErrorMessage: (s: Schema) => s.errorMessage?.enum,
		valueType: "array",
	},
	[RuleOperator.NUM_EQ]: {
		operandTypes: [OperandType.NUMBER],
		value: RuleOperator.NUM_EQ,
		label: { single: "be equal to" },
		fullLabel: { numeric: "be equal to (=)" },
		getSchema: (value, errorMsg) => ({ type: "number", const: value, errorMessage: { const: errorMsg } }),
		getValue: (s: Schema) => toNumber(s.const),
		getErrorMessage: (s: Schema) => s.errorMessage?.const,
		valueType: "number",
	},
	[RuleOperator.STRING_NEQ]: {
		operandTypes: [OperandType.TEXT],
		value: RuleOperator.STRING_NEQ,
		label: { single: "not be equal to", multiple: "not be equal to any of" },
		fullLabel: { alpha: "not be equal to" },
		getSchema: (values, errorMsg) => {
			return { type: "string", not: { enum: Array.isArray(values) ? values : [values] }, errorMessage: { not: errorMsg } };
		},
		getValue: (s: Schema): string[] => toStringArray(s.not?.enum),
		getErrorMessage: (s: Schema) => s.errorMessage?.not,
		valueType: "array",
	},
	[RuleOperator.NUM_NEQ]: {
		operandTypes: [OperandType.NUMBER],
		value: RuleOperator.NUM_NEQ,
		label: { single: "not be equal to" },
		fullLabel: { numeric: "not be equal to (≠)" },
		getSchema: (value, errorMsg) => ({ type: "number", not: { const: value }, errorMessage: { not: errorMsg } }),
		getValue: (s: Schema) => toNumber(s.not?.const),
		getErrorMessage: (s: Schema) => s.errorMessage?.not,
		valueType: "number",
	},
	[RuleOperator.BEGINS]: {
		operandTypes: [OperandType.TEXT],
		value: RuleOperator.BEGINS,
		label: { single: "begin with", multiple: "begin with one of" },
		fullLabel: { alpha: "begin with" },
		getSchema: (values, errorMsg) => ({
			type: "string",
			pattern: `^(${toPatternGroup(toValueArray(values))})`,
			errorMessage: { pattern: errorMsg },
		}),
		getValue: (s: Schema): string[] => {
			const group = extractGroup(s.pattern ?? "");
			return group ? fromPatternGroup(group) : [];
		},
		getErrorMessage: (s: Schema) => s.errorMessage?.pattern,
		valueType: "array",
	},
	[RuleOperator.ENDS]: {
		operandTypes: [OperandType.TEXT],
		value: RuleOperator.ENDS,
		label: { single: "end with", multiple: "end with one of" },
		fullLabel: { alpha: "end with" },
		getSchema: (values, errorMsg) => ({
			type: "string",
			pattern: `(${toPatternGroup(toValueArray(values))})$`,
			errorMessage: { pattern: errorMsg },
		}),
		getValue: (s: Schema): string[] => {
			const group = extractGroup(s.pattern ?? "");
			return group ? fromPatternGroup(group) : [];
		},
		getErrorMessage: (s: Schema) => s.errorMessage?.pattern,
		valueType: "array",
	},
	[RuleOperator.CHAR_COUNT_EXACT]: {
		operandTypes: [OperandType.TEXT],
		value: RuleOperator.CHAR_COUNT_EXACT,
		label: { single: "have" },
		fullLabel: { alpha: "have [x] characters", numeric: "have [x] digits" },
		getSchema: (value, errorMsg, type) => ({ type, minLength: Number(value ?? 0), maxLength: Number(value ?? 0), errorMessage: { minLength: errorMsg, maxLength: errorMsg } }),
		getValue: (s: Schema) => s.minLength,
		charCountSecondaryLabel: "exactly",
		getErrorMessage: (s: Schema) => s.errorMessage?.minLength,
		valueType: "number",
	},
	[RuleOperator.CHAR_COUNT_MIN]: {
		operandTypes: [],
		value: RuleOperator.CHAR_COUNT_MIN,
		label: { single: "have" },
		fullLabel: { alpha: "have [x] characters", numeric: "have [x] digits" },
		getSchema: (value, errorMsg, type) => ({ type, minLength: Number(value ?? 0), errorMessage: { minLength: errorMsg } }),
		getValue: (s: Schema) => s.minLength,
		charCountSecondaryLabel: "minimum",
		getErrorMessage: (s: Schema) => s.errorMessage?.minLength,
		valueType: "number",
	},
	[RuleOperator.CHAR_COUNT_MAX]: {
		operandTypes: [],
		value: RuleOperator.CHAR_COUNT_MAX,
		label: { single: "have" },
		fullLabel: { alpha: "have [x] characters", numeric: "have [x] digits" },
		getSchema: (value, errorMsg, type) => ({ type, maxLength: Number(value ?? 0), errorMessage: { maxLength: errorMsg } }),
		getValue: (s: Schema) => s.maxLength,
		charCountSecondaryLabel: "maximum",
		getErrorMessage: (s: Schema) => s.errorMessage?.maxLength,
		valueType: "number",
	},
	[RuleOperator.DIGIT_COUNT_EXACT]: {
		operandTypes: [OperandType.NUMBER],
		value: RuleOperator.DIGIT_COUNT_EXACT,
		label: { single: "have" },
		fullLabel: { numeric: "have [x] digits" },
		getSchema: (value, errorMsg, type) => ({ type, minimum: Math.pow(10, Number(value ?? 0) - 1), maximum: Math.pow(10, Number(value ?? 0)) - 1, errorMessage: { minimum: errorMsg, maximum: errorMsg } }),
		getValue: (s: Schema) => Math.floor(Math.log10(Number(s.minimum ?? 0))) + 1,
		charCountSecondaryLabel: "exactly",
		getErrorMessage: (s: Schema) => s.errorMessage?.minimum,
		valueType: "number",
	},
	[RuleOperator.DIGIT_COUNT_MIN]: {
		operandTypes: [],
		value: RuleOperator.DIGIT_COUNT_MIN,
		label: { single: "have" },
		fullLabel: { numeric: "have [x] digits" },
		getSchema: (value, errorMsg, type) => ({ type, minimum: Math.pow(10, Number(value ?? 0) - 1), errorMessage: { minimum: errorMsg } }),
		getValue: (s: Schema) => Math.floor(Math.log10(Number(s.minimum))) + 1,
		charCountSecondaryLabel: "minimum",
		getErrorMessage: (s: Schema) => s.errorMessage?.minimum,
		valueType: "number",
	},
	[RuleOperator.DIGIT_COUNT_MAX]: {
		operandTypes: [],
		value: RuleOperator.DIGIT_COUNT_MAX,
		label: { single: "have" },
		fullLabel: { numeric: "have [x] digits" },
		getSchema: (value, errorMsg, type) => ({ type, maximum: Math.pow(10, Number(value ?? 0)) - 1, errorMessage: { maximum: errorMsg } }),
		getValue: (s: Schema) => Math.floor(Math.log10(Number(s.maximum ?? 0) + 1)),
		charCountSecondaryLabel: "maximum",
		getErrorMessage: (s: Schema) => s.errorMessage?.maximum,
		valueType: "number",
	},
	[RuleOperator.LT]: {
		operandTypes: [OperandType.NUMBER],
		value: RuleOperator.LT,
		label: { single: "be less than" },
		fullLabel: { numeric: "be less than (<)" },
		getSchema: (value, errorMsg) => ({ type: "number", exclusiveMaximum: Number(value), errorMessage: { exclusiveMaximum: errorMsg } }),
		getValue: (s: Schema) => s.exclusiveMaximum as number,
		getErrorMessage: (s: Schema) => s.errorMessage?.exclusiveMaximum,
		valueType: "number",
	},
	[RuleOperator.LTE]: {
		operandTypes: [OperandType.NUMBER],
		value: RuleOperator.LTE,
		label: { single: "be less or equal to" },
		fullLabel: { numeric: "be less or equal to (≤)" },
		getSchema: (value, errorMsg) => ({ type: "number", maximum: Number(value ?? 0), errorMessage: { maximum: errorMsg } }),
		getValue: (s: Schema) => s.maximum,
		getErrorMessage: (s: Schema) => s.errorMessage?.maximum,
		valueType: "number",
	},
	[RuleOperator.GT]: {
		operandTypes: [OperandType.NUMBER],
		value: RuleOperator.GT,
		label: { single: "be greater than" },
		fullLabel: { numeric: "be greater than (>)" },
		getSchema: (value, errorMsg) => ({ type: "number", exclusiveMinimum: Number(value ?? 0), errorMessage: { exclusiveMinimum: errorMsg } }),
		getValue: (s: Schema) => s.exclusiveMinimum as number,
		getErrorMessage: (s: Schema) => s.errorMessage?.exclusiveMinimum,
		valueType: "number",
	},
	[RuleOperator.GTE]: {
		operandTypes: [OperandType.NUMBER],
		value: RuleOperator.GTE,
		label: { single: "be greater or equal to" },
		fullLabel: { numeric: "be greater or equal to (≥)" },
		getSchema: (value, errorMsg) => ({ type: "number", minimum: Number(value ?? 0), errorMessage: { minimum: errorMsg } }),
		getValue: (s: Schema) => s.minimum,
		getErrorMessage: (s: Schema) => s.errorMessage?.minimum,
		valueType: "number",
	},
	[RuleOperator.BETWEEN]: {
		operandTypes: [OperandType.NUMBER],
		value: RuleOperator.BETWEEN,
		label: { single: "be in between" },
		fullLabel: { numeric: "be in between (x~y)" },
		getSchema: (value, errorMsg) => ({ type: "number", minimum: value ? Number((value as BetweenValue).min) : undefined, maximum: value ? Number((value as BetweenValue).max) : undefined, errorMessage: { minimum: errorMsg, maximum: errorMsg } }),
		getValue: (s: Schema) => ({ min: s.minimum, max: s.maximum }) as BetweenValue,
		getErrorMessage: (s: Schema) => s.errorMessage?.minimum,
		valueType: "object",
	},
	[RuleOperator.REGEX]: {
		operandTypes: [OperandType.TEXT],
		value: RuleOperator.REGEX,
		label: { single: "match regex" },
		fullLabel: { alpha: "match regex" },
		getSchema: (value, errorMsg) => ({ type: "string", pattern: value as string, errorMessage: { pattern: errorMsg } }),
		getValue: (s: Schema) => (typeof s.pattern === "string" ? s.pattern : undefined),
		getErrorMessage: (s: Schema) => s.errorMessage?.pattern,
		valueType: "string",
	},
	[RuleOperator.DATE_AT_LEAST]: {
		operandTypes: [OperandType.DATE],
		value: RuleOperator.DATE_AT_LEAST,
		label: { single: "be at least" },
		fullLabel: { alpha: "be at least [x] ago" },
		getSchema: (value, errorMsg) => {
			const durationValue = value as DurationValue;
			return {
				duration: { ...durationValue, operator: RuleOperator.DATE_AT_LEAST },
				errorMessage: { pattern: errorMsg },
			};
		},
		getValue: (s: Schema): DurationValue => {
			return s.duration as DurationValue;
		},
		getErrorMessage: (s: Schema) => s.errorMessage?.pattern,
		valueType: "string",
	},
	[RuleOperator.DATE_NO_MORE_THAN]: {
		operandTypes: [OperandType.DATE],
		value: RuleOperator.DATE_NO_MORE_THAN,
		label: { single: "be no more than" },
		fullLabel: { alpha: "be no more than [x] ago" },
		getSchema: (value, errorMsg) => {
			const durationValue = value as DurationValue;
			return {
				duration: { ...durationValue, operator: RuleOperator.DATE_NO_MORE_THAN },
				errorMessage: { pattern: errorMsg },
			};
		},
		getValue: (s: Schema): DurationValue => {
			return s.duration as DurationValue;
		},
		getErrorMessage: (s: Schema) => s.errorMessage?.pattern,
		valueType: "string",
	},
	[RuleOperator.DATE_WITHIN]: {
		operandTypes: [OperandType.DATE],
		value: RuleOperator.DATE_WITHIN,
		label: { single: "be within" },
		fullLabel: { alpha: "be within [x] after today" },
		getSchema: (value, errorMsg) => {
			const durationValue = value as DurationValue;
			return {
				duration: { ...durationValue, operator: RuleOperator.DATE_WITHIN },
				errorMessage: { pattern: errorMsg },
			};
		},
		getValue: (s: Schema): DurationValue => {
			return s.duration as DurationValue;
		},
		getErrorMessage: (s: Schema) => s.errorMessage?.pattern,
		valueType: "string",
	},
	[RuleOperator.DATE_MORE_THAN]: {
		operandTypes: [OperandType.DATE],
		value: RuleOperator.DATE_MORE_THAN,
		label: { single: "be more than" },
		fullLabel: { alpha: "be after [x] from today" },
		getSchema: (value, errorMsg) => {
			const durationValue = value as DurationValue;
			return {
				duration: { ...durationValue, operator: RuleOperator.DATE_MORE_THAN },
				errorMessage: { pattern: errorMsg },
			};
		},
		getValue: (s: Schema): DurationValue => {
			return s.duration as DurationValue;
		},
		getErrorMessage: (s: Schema) => s.errorMessage?.pattern,
		valueType: "string",
	},
	[RuleOperator.DATE_BETWEEN]: {
		operandTypes: [OperandType.DATE],
		value: RuleOperator.DATE_BETWEEN,
		label: { single: "be in between" },
		fullLabel: { alpha: "be in between" },
		getSchema: (value, errorMsg) => {
			const between = value as BetweenValue;
			return { type: "string", format: "date", formatMinimum: between?.min, formatMaximum: between?.max, errorMessage: { minimum: errorMsg, maximum: errorMsg } };
		},
		getValue: (s: Schema) => ({ min: s.formatMinimum, max: s.formatMaximum }) as BetweenValue,
		getErrorMessage: (s: Schema) => s.errorMessage?.minimum,
		valueType: "object",
	},
	[RuleOperator.DATE_EQ]: {
		operandTypes: [OperandType.DATE],
		value: RuleOperator.DATE_EQ,
		label: { single: "be equal to" },
		fullLabel: { alpha: "be equal to" },
		getSchema: (value, errorMsg) => ({ type: "string", format: "date", const: value, errorMessage: { const: errorMsg } }),
		getValue: (s: Schema): string => s.const as string,
		getErrorMessage: (s: Schema) => s.errorMessage?.const,
		valueType: "string",
	},
	[RuleOperator.DATE_NEQ]: {
		operandTypes: [OperandType.DATE],
		value: RuleOperator.DATE_NEQ,
		label: { single: "not be equal to" },
		fullLabel: { alpha: "not be equal to" },
		getSchema: (value, errorMsg) => {
			return { type: "string", format: "date", not: { const: value }, errorMessage: { not: errorMsg } };
		},
		getValue: (s: Schema): string => s.not?.const as string,
		getErrorMessage: (s: Schema) => s.errorMessage?.not,
		valueType: "string",
	},
};

export type Operator = (typeof RuleOperator)[keyof typeof RuleOperator];

export const operatorTypes = Object.keys(operators) as Operator[];

export const multiTokenTypes = Object.keys(operators).filter(key => Boolean(operators[key as RuleOperator].label?.multiple));

type SchemaProducer = (value: string | number | undefined | BetweenValue | string[] | DurationValue, errorMsg: string, type: OperandType.TEXT | OperandType.NUMBER) => Schema;

export function parseSchema(s: Schema): DefineValidationSchemaType {
	if (!s || !(s.anyOf || s.allOf)) {
		return { rules: [], validationType: "all", errorMessage: defaultErrorMessage, zeroBounce: s?.zeroBounce };
	}
	const properties = s.anyOf ? s.anyOf : s.allOf;
	const rules: { operator: RuleOperator; value: string | number | undefined | BetweenValue | string[] | DurationValue }[] = [];
	let errorMessage = "";
	(properties ?? []).forEach(property => {
		const operator = property.description as RuleOperator;
		const operand = operators[operator].getValue?.(property);
		if (errorMessage.length === 0) {
			const operatorError = operators[operator].getErrorMessage?.(property);
			if (operatorError) {
				errorMessage = operatorError;
			}
		}
		rules.push({ operator, value: operand });
	});
	return { rules, validationType: s.anyOf ? "any" : "all", errorMessage: errorMessage, zeroBounce: s.zeroBounce };
}

export function generateSchema(parsedSchema: DefineValidationSchemaType, fieldType: FieldType): Schema {
	const rules = parsedSchema.rules.map(rule => ({
		description: rule.operator,
		...operators[rule.operator].getSchema(rule.value, parsedSchema.errorMessage, fieldType === FieldType.NUMBER ? OperandType.NUMBER : OperandType.TEXT),
	}));
	if (parsedSchema.validationType === "any") {
		return {
			...(rules.length > 0 && { anyOf: rules }),
			...(parsedSchema.zeroBounce && { zeroBounce: parsedSchema.zeroBounce }),
		};
	} else {
		return {
			...(rules.length > 0 && { allOf: rules }),
			...(parsedSchema.zeroBounce && { zeroBounce: parsedSchema.zeroBounce }),
		};
	}
}

export function isZeroBounceEnabled(s: string | undefined) {
	if (!s) return false;
	if (!s.length) return false;
	const parsedSchema = parseSchema(JSON.parse(s));
	return parsedSchema.zeroBounce;
}

export function hasValidationRules(s: string | undefined) {
	if (!s) return false;
	if (s && s.length === 0) return false;
	const parsedSchema = parseSchema(JSON.parse(s));
	return parsedSchema.rules.length > 0;
}
export interface Rule {
	operator: RuleOperator;
	value: string | number | undefined | BetweenValue | string[] | DurationValue;
}

export interface DefineValidationSchemaType {
	validationType: "any" | "all";
	errorMessage: string;
	rules: Rule[];
	zeroBounce?: boolean;
}

export function getOperatorFullLabel(operator: Operator, fieldType: FieldType, tokenCount?: number): string {
	if (fieldType === FieldType.NUMBER) {
		return operators[operator].fullLabel?.numeric || "";
	}
	return tokenCount && tokenCount > 1 && operators[operator].label?.multiple ? operators[operator].label?.multiple || "" : operators[operator].fullLabel?.alpha || "";
}

export function getOperatorLabel(operator: Operator, tokenCount?: number): string {
	const label = operators[operator].label;
	if (!label.multiple || !tokenCount) {
		return label.single;
	}
	return tokenCount > 1 ? label.multiple : label.single;
}

const regexSpecialChars = /[.*+?^${}()|[\]\\]/g;

function escapeForRegex(str: string): string {
	return str.replace(regexSpecialChars, "\\$&");
}

function unescapeRegex(str: string): string {
	return str.replace(/\\(.)/g, "$1");
}

function toPatternGroup(values: string[]): string {
	const escaped = values.filter(Boolean).map(escapeForRegex);
	return escaped.join("|");
}

function fromPatternGroup(pattern: string): string[] {
	return pattern.split("|").map(unescapeRegex).filter(Boolean);
}

function extractGroup(pattern: string): string | null {
	const match = pattern.match(/^\^?\((.*?)\)\$?$/);
	return match?.[1] ?? null;
}

function toValueArray(val: RuleValueType): string[] {
	if (val === undefined) return [];
	if (Array.isArray(val)) return val.filter(Boolean) as string[];
	if (typeof val === "string") return [val];
	return [String(val)];
}

export const countOperators = [RuleOperator.CHAR_COUNT_EXACT, RuleOperator.CHAR_COUNT_MIN, RuleOperator.CHAR_COUNT_MAX, RuleOperator.DIGIT_COUNT_EXACT, RuleOperator.DIGIT_COUNT_MIN, RuleOperator.DIGIT_COUNT_MAX];

export const charCountOptions = [
	{ value: RuleOperator.CHAR_COUNT_EXACT, label: "exactly" },
	{ value: RuleOperator.CHAR_COUNT_MIN, label: "minimum" },
	{ value: RuleOperator.CHAR_COUNT_MAX, label: "maximum" },
];

export const digitCountOptions = [
	{ value: RuleOperator.DIGIT_COUNT_EXACT, label: "exactly" },
	{ value: RuleOperator.DIGIT_COUNT_MIN, label: "minimum" },
	{ value: RuleOperator.DIGIT_COUNT_MAX, label: "maximum" },
];

export const durationOptions = Object.values(DurationUnit);
export const relativeDateOperators: Set<RuleOperator> = new Set([RuleOperator.DATE_AT_LEAST, RuleOperator.DATE_WITHIN, RuleOperator.DATE_MORE_THAN, RuleOperator.DATE_NO_MORE_THAN]);

export const defaultErrorMessage = "Please enter a valid input";

const jsRegexLiteralPattern = /^\/(.*)\/([a-zA-Z]*)$/s;

/**
 * Validates a user-entered value for a JSON Schema `pattern`.
 * JSON Schema pattern strings must be a raw ECMA-262 regex source, e.g. `^\d{5}$`,
 * not a JS regex literal like `/^\d{5}$/u`.
 */
export function getRegexPatternError(value: string): string | undefined {
	if (!value) {
		return undefined;
	}

	if (jsRegexLiteralPattern.test(value)) {
		return 'Enter a raw pattern, not a JS regex literal. Remove the surrounding "/" delimiters and flags.';
	}

	try {
		new RegExp(value, "u");
	} catch {
		return String.raw`Not a valid regular expression. Enter a raw pattern with no surrounding slashes or flags, e.g. ^\d{5}$.`;
	}

	return undefined;
}
